import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Trash2, Plus, ChevronDown, ChevronRight, AlertTriangle,
  MessageSquare, GitBranch, ListChecks, Shield, Table2, LayoutGrid, Image as ImageIcon,
  GripVertical, ArrowUp, ArrowDown
} from 'lucide-react';
import { RichParaEditor } from './RichParaEditor';
import { CalsTableEditor, parseCalsTable } from './CalsTableEditor';
import { CaptionGroupEditor, parseCaptionGroup } from './CaptionGroupEditor';
import { HotspotEditor, parseHotspotEl } from './HotspotEditor';

const uid = () => Math.random().toString(36).substring(2, 10);

/* ═══════════════════════════════════════════
   CREW DRILL STEP EDITOR
   Step types: challengeAndResponse, crewProcedureName, or para
   Each text field supports rich inline content (emphasis, refs, lists)
   ═══════════════════════════════════════════ */
const CrewDrillStepEditor = ({ step, onUpdate, onDelete, dmlEntries = [], dirFiles = [], internalTargets = [], stepNumber = null, index = 0, count = 0, onMoveUp = null, onMoveDown = null, dragHandleProps = null }) => {
  const stepType = step.stepType || 'challengeAndResponse';
  const [collapsed, setCollapsed] = useState(false);

  const handleFieldChange = (field, { text, inlineRefs, inlineList }) => {
    onUpdate({
      ...step,
      [field]: text,
      [`${field}Refs`]: inlineRefs && inlineRefs.length > 0 ? inlineRefs : undefined,
      [`${field}List`]: inlineList || undefined,
    });
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-white group hover:border-slate-300 transition-colors" data-testid={`crew-drill-step-${step.id}`}>
      {/* crewDrillStep wrapper header */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-cyan-50/70 rounded-t-lg border-b border-slate-200">
        <div className="flex items-center gap-1.5 min-w-0">
          {dragHandleProps && (
            <span {...dragHandleProps} className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 shrink-0" title="Drag to reorder" data-testid={`drag-crew-step-${step.id}`}>
              <GripVertical className="w-3.5 h-3.5" />
            </span>
          )}
          <button onClick={() => setCollapsed(!collapsed)} className="flex items-center gap-1.5 min-w-0" data-testid={`toggle-crew-step-${step.id}`}>
            {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
            <Badge className="text-[10px] bg-cyan-700 text-white hover:bg-cyan-700">crewDrillStep</Badge>
            {stepNumber != null && <span className="text-[11px] text-slate-500 font-medium shrink-0">Step {stepNumber}</span>}
            {stepType === 'challengeAndResponse' ? (
              <Badge className="text-[10px] bg-cyan-100 text-cyan-700">Challenge / Response</Badge>
            ) : stepType === 'crewProcedureName' ? (
              <Badge className="text-[10px] bg-indigo-100 text-indigo-700">Procedure Name</Badge>
            ) : (
              <Badge className="text-[10px] bg-slate-100 text-slate-700">Para</Badge>
            )}
          </button>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {onMoveUp && (
            <button onClick={onMoveUp} disabled={index === 0} className="p-1 text-slate-400 hover:text-slate-700 rounded hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent" title="Move up" data-testid={`move-crew-step-up-${step.id}`}>
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
          )}
          {onMoveDown && (
            <button onClick={onMoveDown} disabled={index >= count - 1} className="p-1 text-slate-400 hover:text-slate-700 rounded hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent" title="Move down" data-testid={`move-crew-step-down-${step.id}`}>
              <ArrowDown className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={onDelete} className="p-1 text-slate-300 hover:text-red-500" data-testid={`delete-step-${step.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {!collapsed && (
      <div className="p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Select value={stepType} onValueChange={v => onUpdate({ ...step, stepType: v })}>
            <SelectTrigger className="h-6 text-[10px] w-[160px] bg-white" data-testid={`step-type-select-${step.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="challengeAndResponse">Challenge/Response</SelectItem>
              <SelectItem value="crewProcedureName">Procedure Name</SelectItem>
              <SelectItem value="para">Para (Text)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {stepType === 'challengeAndResponse' ? (
        <div className="space-y-4">
          {/* ── CHALLENGE ── */}
          <div className="space-y-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-cyan-700 border-b border-cyan-200 pb-1">
              Challenge <span className="font-normal text-slate-400">(optional)</span>
            </h4>
            <RichParaEditor
              value={step.challenge || ''}
              inlineRefs={step.challengeRefs || []}
              inlineList={step.challengeList || null}
              onChange={(d) => handleFieldChange('challenge', d)}
              dmlEntries={dmlEntries} dirFiles={dirFiles} internalTargets={internalTargets}
              label="Para"
              placeholder="Challenge para text (required by schema)..."
              rows={2} compact id={`challenge-${step.id}`}
            />
            {/* captionGroups inside challenge para */}
            {(step.challengeCaptionGroups || []).map((cg, idx) => (
              <div key={cg.id || idx} className="ml-3 border-l-2 border-cyan-200 pl-3 space-y-1">
                <span className="text-[10px] text-cyan-600 uppercase tracking-wider font-semibold">
                  captionGroup {(step.challengeCaptionGroups || []).length > 1 ? `#${idx + 1}` : ''} <span className="text-slate-400 normal-case">(child of para)</span>
                </span>
                <CaptionGroupEditor
                  element={cg}
                  onUpdate={(updated) => {
                    const cgs = [...(step.challengeCaptionGroups || [])];
                    cgs[idx] = updated;
                    onUpdate({ ...step, challengeCaptionGroups: cgs });
                  }}
                  onDelete={() => {
                    const cgs = (step.challengeCaptionGroups || []).filter((_, i) => i !== idx);
                    onUpdate({ ...step, challengeCaptionGroups: cgs.length > 0 ? cgs : undefined });
                  }}
                  compact
                />
              </div>
            ))}
            {step.challengeTable && (
              <div className="ml-3 border-l-2 border-cyan-200 pl-3 space-y-1">
                <span className="text-[10px] text-cyan-600 uppercase tracking-wider font-semibold">Table</span>
                <CalsTableEditor
                  element={step.challengeTable}
                  onUpdate={(t) => onUpdate({ ...step, challengeTable: t })}
                  onDelete={() => onUpdate({ ...step, challengeTable: undefined })}
                  compact
                />
              </div>
            )}
            <div className="flex gap-2 flex-wrap ml-3">
              <Button variant="ghost" size="sm" className="text-[10px] h-5 gap-1 text-cyan-600"
                data-testid={`add-challenge-cg-${step.id}`}
                onClick={() => {
                  const cgs = [...(step.challengeCaptionGroups || [])];
                  cgs.push({ id: uid(), type: 'captionGroup', cols: 2, applicRefId: '', colspecs: [{ colnum: '1', colname: 'col1', colwidth: '30mm' }, { colnum: '2', colname: 'col2', colwidth: '30mm' }], rows: [[{ colname: 'col1', color: 'co04', captionWidth: '30mm', text: '' }, { colname: 'col2', color: 'co51', captionWidth: '30mm', text: '' }]] });
                  onUpdate({ ...step, challengeCaptionGroups: cgs });
                }}>
                <Plus className="w-2.5 h-2.5" /> captionGroup
              </Button>
              {!step.challengeTable && (
                <Button variant="ghost" size="sm" className="text-[10px] h-5 gap-1 text-slate-500"
                  onClick={() => onUpdate({ ...step, challengeTable: { id: uid(), type: 'table', title: '', cols: 2, colspecs: [{ colname: 'col1', colwidth: '1*' }, { colname: 'col2', colwidth: '1*' }], thead: [['', '']], tbody: [['', '']] } })}>
                  <Plus className="w-2.5 h-2.5" /> Table
                </Button>
              )}
            </div>
          </div>

          {/* ── RESPONSE ── */}
          <div className="space-y-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-violet-700 border-b border-violet-200 pb-1">
              Response
            </h4>
            <RichParaEditor
              value={step.response || ''}
              inlineRefs={step.responseRefs || []}
              inlineList={step.responseList || null}
              onChange={(d) => handleFieldChange('response', d)}
              dmlEntries={dmlEntries} dirFiles={dirFiles} internalTargets={internalTargets}
              label="Para"
              placeholder="Response para text..."
              rows={2} compact id={`response-${step.id}`}
            />
            {/* captionGroups inside response para */}
            {(step.responseCaptionGroups || []).map((cg, idx) => (
              <div key={cg.id || idx} className="ml-3 border-l-2 border-violet-200 pl-3 space-y-1">
                <span className="text-[10px] text-violet-600 uppercase tracking-wider font-semibold">
                  captionGroup {(step.responseCaptionGroups || []).length > 1 ? `#${idx + 1}` : ''} <span className="text-slate-400 normal-case">(child of para)</span>
                </span>
                <CaptionGroupEditor
                  element={cg}
                  onUpdate={(updated) => {
                    const cgs = [...(step.responseCaptionGroups || [])];
                    cgs[idx] = updated;
                    onUpdate({ ...step, responseCaptionGroups: cgs });
                  }}
                  onDelete={() => {
                    const cgs = (step.responseCaptionGroups || []).filter((_, i) => i !== idx);
                    onUpdate({ ...step, responseCaptionGroups: cgs.length > 0 ? cgs : undefined });
                  }}
                  compact
                />
              </div>
            ))}
            {step.responseTable && (
              <div className="ml-3 border-l-2 border-violet-200 pl-3 space-y-1">
                <span className="text-[10px] text-violet-600 uppercase tracking-wider font-semibold">Table</span>
                <CalsTableEditor
                  element={step.responseTable}
                  onUpdate={(t) => onUpdate({ ...step, responseTable: t })}
                  onDelete={() => onUpdate({ ...step, responseTable: undefined })}
                  compact
                />
              </div>
            )}
            <div className="flex gap-2 flex-wrap ml-3">
              <Button variant="ghost" size="sm" className="text-[10px] h-5 gap-1 text-violet-600"
                data-testid={`add-response-cg-${step.id}`}
                onClick={() => {
                  const cgs = [...(step.responseCaptionGroups || [])];
                  cgs.push({ id: uid(), type: 'captionGroup', cols: 2, applicRefId: '', colspecs: [{ colnum: '1', colname: 'col1', colwidth: '30mm' }, { colnum: '2', colname: 'col2', colwidth: '30mm' }], rows: [[{ colname: 'col1', color: 'co04', captionWidth: '30mm', text: '' }, { colname: 'col2', color: 'co51', captionWidth: '30mm', text: '' }]] });
                  onUpdate({ ...step, responseCaptionGroups: cgs });
                }}>
                <Plus className="w-2.5 h-2.5" /> captionGroup
              </Button>
              {!step.responseTable && (
                <Button variant="ghost" size="sm" className="text-[10px] h-5 gap-1 text-slate-500"
                  onClick={() => onUpdate({ ...step, responseTable: { id: uid(), type: 'table', title: '', cols: 2, colspecs: [{ colname: 'col1', colwidth: '1*' }, { colname: 'col2', colwidth: '1*' }], thead: [['', '']], tbody: [['', '']] } })}>
                  <Plus className="w-2.5 h-2.5" /> Table
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : stepType === 'crewProcedureName' ? (
        <RichParaEditor
          value={step.procedureName || ''}
          inlineRefs={step.procedureNameRefs || []}
          inlineList={step.procedureNameList || null}
          onChange={(d) => handleFieldChange('procedureName', d)}
          dmlEntries={dmlEntries} dirFiles={dirFiles} internalTargets={internalTargets}
          label="Procedure Name"
          placeholder="Procedure name text..."
          rows={1} compact id={`procname-${step.id}`}
        />
      ) : (
        <RichParaEditor
          value={step.paraText || ''}
          inlineRefs={step.paraTextRefs || []}
          inlineList={step.paraTextList || null}
          onChange={(d) => handleFieldChange('paraText', d)}
          dmlEntries={dmlEntries} dirFiles={dirFiles} internalTargets={internalTargets}
          label="Paragraph Text"
          placeholder="Paragraph text..."
          rows={2} compact id={`para-${step.id}`}
        />
      )}
      </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   CONDITIONAL STEP EDITOR (if / elseIf / case)
   ═══════════════════════════════════════════ */
const ConditionalStepEditor = ({ item, onUpdate, onDelete, dmlEntries = [], dirFiles = [], internalTargets = [] }) => {
  const condType = item.type;
  const colorMap = {
    'if': { bg: 'bg-yellow-50 border-yellow-200', badge: 'bg-yellow-100 text-yellow-700', label: 'IF' },
    'elseIf': { bg: 'bg-orange-50 border-orange-200', badge: 'bg-orange-100 text-orange-700', label: 'ELSE IF' },
    'case': { bg: 'bg-purple-50 border-purple-200', badge: 'bg-purple-100 text-purple-700', label: 'CASE' },
  };
  const colors = colorMap[condType] || colorMap['if'];

  return (
    <div className={`border rounded-lg p-3 ${colors.bg} group`} data-testid={`conditional-${item.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <GitBranch className="w-3.5 h-3.5 text-yellow-600" />
          <Badge className={`text-[10px] ${colors.badge}`}>{colors.label}</Badge>
        </div>
        <button onClick={onDelete} className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="space-y-2">
        <div className="space-y-1">
          <Label className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Condition</Label>
          <Input
            value={item.condition || ''}
            onChange={e => onUpdate({ ...item, condition: e.target.value })}
            placeholder="Condition text (caseCond)..."
            className="text-sm h-8 bg-white"
            data-testid={`cond-text-${item.id}`}
          />
        </div>
        <CrewDrillStepEditor
          step={{
            id: item.id + '-step', stepType: item.stepType || 'challengeAndResponse',
            challenge: item.challenge || '', challengeRefs: item.challengeRefs, challengeList: item.challengeList,
            challengeCaptionGroups: item.challengeCaptionGroups, challengeTable: item.challengeTable,
            response: item.response || '', responseRefs: item.responseRefs, responseList: item.responseList,
            responseCaptionGroups: item.responseCaptionGroups, responseTable: item.responseTable,
            procedureName: item.procedureName || '', procedureNameRefs: item.procedureNameRefs, procedureNameList: item.procedureNameList,
            paraText: item.paraText || '', paraTextRefs: item.paraTextRefs, paraTextList: item.paraTextList,
          }}
          onUpdate={(s) => onUpdate({
            ...item, stepType: s.stepType,
            challenge: s.challenge, challengeRefs: s.challengeRefs, challengeList: s.challengeList,
            challengeCaptionGroups: s.challengeCaptionGroups, challengeTable: s.challengeTable,
            response: s.response, responseRefs: s.responseRefs, responseList: s.responseList,
            responseCaptionGroups: s.responseCaptionGroups, responseTable: s.responseTable,
            procedureName: s.procedureName, procedureNameRefs: s.procedureNameRefs, procedureNameList: s.procedureNameList,
            paraText: s.paraText, paraTextRefs: s.paraTextRefs, paraTextList: s.paraTextList,
          })}
          onDelete={onDelete}
          dmlEntries={dmlEntries}
          dirFiles={dirFiles}
          internalTargets={internalTargets}
        />
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   SUB CREW DRILL EDITOR
   Contains title + crewDrillSteps
   ═══════════════════════════════════════════ */
const SubCrewDrillEditor = ({ subDrill, onUpdate, onDelete, dmlEntries = [], dirFiles = [], internalTargets = [], index = 0, count = 0, onMoveUp = null, onMoveDown = null, dragHandleProps = null }) => {
  const [collapsed, setCollapsed] = useState(false);

  const addStep = () => {
    const steps = [...(subDrill.steps || []), { id: uid(), stepType: 'challengeAndResponse', challenge: '', response: '' }];
    onUpdate({ ...subDrill, steps });
  };

  const updateStep = (idx, step) => {
    const steps = [...(subDrill.steps || [])];
    steps[idx] = step;
    onUpdate({ ...subDrill, steps });
  };

  const deleteStep = (idx) => {
    onUpdate({ ...subDrill, steps: (subDrill.steps || []).filter((_, i) => i !== idx) });
  };

  const dragStepIdx = React.useRef(null);
  const moveStep = (from, to) => {
    const steps = [...(subDrill.steps || [])];
    if (from === null || from === undefined || from === to || to < 0 || to >= steps.length) return;
    const [it] = steps.splice(from, 1);
    steps.splice(to, 0, it);
    onUpdate({ ...subDrill, steps });
  };

  return (
    <div className="border border-teal-200 rounded-lg bg-teal-50/30" data-testid={`sub-drill-${subDrill.id}`}>
      <div className="flex items-center justify-between px-3 py-2 bg-teal-50 rounded-t-lg border-b border-teal-200 cursor-pointer" onClick={() => setCollapsed(!collapsed)}>
        <div className="flex items-center gap-2 text-sm font-medium text-teal-800 min-w-0">
          {dragHandleProps && (
            <span {...dragHandleProps} onClick={(e) => e.stopPropagation()} className="cursor-grab active:cursor-grabbing text-teal-300 hover:text-teal-500 shrink-0" title="Drag to reorder" data-testid={`drag-sub-drill-${subDrill.id}`}>
              <GripVertical className="w-3.5 h-3.5" />
            </span>
          )}
          {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          <Badge className="bg-teal-100 text-teal-700 text-[10px]">subCrewDrill</Badge>
          <span className="truncate max-w-[200px]">{subDrill.title || '(untitled)'}</span>
          <span className="text-xs text-teal-500 font-normal">({(subDrill.steps || []).length} steps)</span>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {onMoveUp && (
            <button onClick={(e) => { e.stopPropagation(); onMoveUp(); }} disabled={index === 0} className="p-1 text-teal-400 hover:text-teal-700 rounded hover:bg-teal-100 disabled:opacity-30 disabled:hover:bg-transparent" title="Move up" data-testid={`move-sub-drill-up-${subDrill.id}`}>
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
          )}
          {onMoveDown && (
            <button onClick={(e) => { e.stopPropagation(); onMoveDown(); }} disabled={index >= count - 1} className="p-1 text-teal-400 hover:text-teal-700 rounded hover:bg-teal-100 disabled:opacity-30 disabled:hover:bg-transparent" title="Move down" data-testid={`move-sub-drill-down-${subDrill.id}`}>
              <ArrowDown className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1 text-teal-400 hover:text-red-500 rounded hover:bg-red-50">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="p-3 space-y-3">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Sub-Drill Title</Label>
            <Input
              value={subDrill.title || ''}
              onChange={e => onUpdate({ ...subDrill, title: e.target.value })}
              placeholder="Sub-drill title..."
              className="text-sm h-8 font-medium"
              data-testid={`sub-drill-title-${subDrill.id}`}
            />
          </div>

          <div className="space-y-2">
            {(subDrill.steps || []).map((step, i) => (
              <div
                key={step.id || i}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); moveStep(dragStepIdx.current, i); dragStepIdx.current = null; }}
              >
                <CrewDrillStepEditor
                  step={step}
                  stepNumber={i + 1}
                  index={i}
                  count={(subDrill.steps || []).length}
                  onMoveUp={() => moveStep(i, i - 1)}
                  onMoveDown={() => moveStep(i, i + 1)}
                  dragHandleProps={{ draggable: true, onDragStart: (e) => { e.stopPropagation(); dragStepIdx.current = i; e.dataTransfer.effectAllowed = 'move'; } }}
                  onUpdate={(s) => updateStep(i, s)}
                  onDelete={() => deleteStep(i)}
                  dmlEntries={dmlEntries}
                  dirFiles={dirFiles}
                  internalTargets={internalTargets}
                />
              </div>
            ))}
          </div>

          <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-teal-600" onClick={addStep} data-testid={`add-sub-step-${subDrill.id}`}>
            <Plus className="w-3 h-3" /> Add Step
          </Button>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   CREW DRILL EDITOR
   Contains title + mix of subCrewDrills, crewDrillSteps, warnings, conditionals
   ═══════════════════════════════════════════ */
export const CrewDrillEditor = ({ drill, index, onUpdate, onDelete, dmlEntries = [], dirFiles = [], dirIcns = [], internalTargets = [], collapsed: collapsedProp, onToggleCollapse }) => {
  const [localCollapsed, setLocalCollapsed] = useState(false);
  const collapsed = onToggleCollapse ? !!collapsedProp : localCollapsed;
  const toggleCollapsed = () => (onToggleCollapse ? onToggleCollapse() : setLocalCollapsed(!localCollapsed));

  const children = drill.children || [];

  const addChild = (type) => {
    let newChild;
    if (type === 'para') {
      newChild = { type: 'para', id: uid(), text: '' };
    } else if (type === 'note') {
      newChild = { type: 'note', id: uid(), text: '' };
    } else if (type === 'step') {
      newChild = { type: 'step', id: uid(), stepType: 'challengeAndResponse', challenge: '', response: '' };
    } else if (type === 'subCrewDrill') {
      newChild = { type: 'subCrewDrill', id: uid(), title: '', steps: [{ id: uid(), stepType: 'challengeAndResponse', challenge: '', response: '' }] };
    } else if (type === 'warning') {
      newChild = { type: 'warning', id: uid(), text: '' };
    } else if (type === 'caution') {
      newChild = { type: 'caution', id: uid(), text: '' };
    } else if (type === 'if') {
      newChild = { type: 'if', id: uid(), condition: '', stepType: 'challengeAndResponse', challenge: '', response: '' };
    } else if (type === 'elseIf') {
      newChild = { type: 'elseIf', id: uid(), condition: '', stepType: 'crewProcedureName', procedureName: '' };
    } else if (type === 'case') {
      newChild = { type: 'case', id: uid(), condition: '', stepType: 'challengeAndResponse', challenge: '', response: '' };
    } else if (type === 'figure') {
      newChild = { type: 'figure', id: uid(), title: '', icn: '' };
    } else if (type === 'table') {
      newChild = { type: 'table', id: uid(), title: '', cols: 3, colspecs: [{ colname: 'col1', colwidth: '1*' }, { colname: 'col2', colwidth: '1*' }, { colname: 'col3', colwidth: '1*' }], thead: [['', '', '']], tbody: [['', '', '']] };
    } else if (type === 'captionGroup') {
      newChild = { type: 'captionGroup', id: uid(), cols: 2, applicRefId: '', colspecs: [{ colnum: '1', colname: 'col1', colwidth: '30mm' }, { colnum: '2', colname: 'col2', colwidth: '30mm' }], rows: [[{ colname: 'col1', color: 'co04', captionWidth: '30mm', text: '' }, { colname: 'col2', color: 'co51', captionWidth: '30mm', text: '' }]] };
    }
    if (newChild) onUpdate({ ...drill, children: [...children, newChild] });
  };

  const updateChild = (idx, child) => {
    const arr = [...children];
    arr[idx] = child;
    onUpdate({ ...drill, children: arr });
  };

  const deleteChild = (idx) => {
    onUpdate({ ...drill, children: children.filter((_, i) => i !== idx) });
  };

  const dragChildIdx = React.useRef(null);
  const moveChild = (from, to) => {
    if (from === null || from === undefined || from === to || to < 0 || to >= children.length) return;
    const arr = [...children];
    const [it] = arr.splice(from, 1);
    arr.splice(to, 0, it);
    onUpdate({ ...drill, children: arr });
  };

  const stepCount = children.filter(c => c.type === 'step' || c.type === 'subCrewDrill').length;

  return (
    <div className="border border-violet-200 rounded-lg bg-white" data-testid={`crew-drill-${index}`}>
      <div className="flex items-center justify-between px-4 py-2.5 bg-violet-50 rounded-t-lg border-b border-violet-200 cursor-pointer" onClick={toggleCollapsed}>
        <div className="flex items-center gap-2 text-sm font-medium text-violet-800">
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          <Badge className="bg-violet-100 text-violet-700 text-[10px]">crewDrill</Badge>
          <span className="truncate max-w-[250px]">{drill.title || '(untitled)'}</span>
          <span className="text-xs text-violet-400 font-normal">({stepCount} items)</span>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1.5 text-violet-400 hover:text-red-500 rounded hover:bg-red-50">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {!collapsed && (
        <div className="p-4 space-y-4">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Drill Title</Label>
            <Input
              value={drill.title || ''}
              onChange={e => onUpdate({ ...drill, title: e.target.value })}
              placeholder="Drill section title..."
              className="text-sm font-medium"
              data-testid={`drill-title-${index}`}
            />
          </div>

          {/* Drill children */}
          <div className="space-y-3">
            {children.map((child, i) => {
              if (child.type === 'step') {
                const stepOrdinal = children.slice(0, i + 1).filter(c => c.type === 'step').length;
                return (
                  <div
                    key={child.id || i}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); moveChild(dragChildIdx.current, i); dragChildIdx.current = null; }}
                    data-testid={`drill-child-drop-${i}`}
                  >
                    <CrewDrillStepEditor
                      step={child}
                      stepNumber={stepOrdinal}
                      index={i}
                      count={children.length}
                      onMoveUp={() => moveChild(i, i - 1)}
                      onMoveDown={() => moveChild(i, i + 1)}
                      dragHandleProps={{ draggable: true, onDragStart: (e) => { e.stopPropagation(); dragChildIdx.current = i; e.dataTransfer.effectAllowed = 'move'; } }}
                      onUpdate={(s) => updateChild(i, { ...s, type: 'step' })}
                      onDelete={() => deleteChild(i)}
                      dmlEntries={dmlEntries}
                      dirFiles={dirFiles}
                      internalTargets={internalTargets}
                    />
                  </div>
                );
              }
              if (child.type === 'subCrewDrill') {
                return (
                  <div
                    key={child.id || i}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); moveChild(dragChildIdx.current, i); dragChildIdx.current = null; }}
                    data-testid={`drill-child-drop-${i}`}
                  >
                    <SubCrewDrillEditor
                      subDrill={child}
                      index={i}
                      count={children.length}
                      onMoveUp={() => moveChild(i, i - 1)}
                      onMoveDown={() => moveChild(i, i + 1)}
                      dragHandleProps={{ draggable: true, onDragStart: (e) => { e.stopPropagation(); dragChildIdx.current = i; e.dataTransfer.effectAllowed = 'move'; } }}
                      onUpdate={(sd) => updateChild(i, sd)}
                      onDelete={() => deleteChild(i)}
                      dmlEntries={dmlEntries}
                      dirFiles={dirFiles}
                      internalTargets={internalTargets}
                    />
                  </div>
                );
              }
              if (child.type === 'para') {
                return (
                  <div key={child.id || i} className="p-3 rounded border border-slate-200 bg-white space-y-2 group" data-testid={`drill-para-${child.id}`}>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-[10px] bg-slate-100">para</Badge>
                      <button onClick={() => deleteChild(i)} className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <Textarea
                      value={child.text || ''}
                      onChange={e => updateChild(i, { ...child, text: e.target.value })}
                      placeholder="Paragraph text..."
                      rows={2}
                      className="text-sm resize-y"
                    />
                  </div>
                );
              }
              if (child.type === 'warning' || child.type === 'caution' || child.type === 'note') {
                const colors = child.type === 'warning'
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : child.type === 'caution'
                    ? 'bg-amber-50 border-amber-200 text-amber-700'
                    : 'bg-blue-50 border-blue-200 text-blue-700';
                return (
                  <div key={child.id || i} className={`p-3 rounded border ${colors} space-y-2 group`} data-testid={`drill-${child.type}-${child.id}`}>
                    <div className="flex items-center justify-between">
                      <Badge className="text-[10px]">{child.type.toUpperCase()}</Badge>
                      <button onClick={() => deleteChild(i)} className="p-1 hover:opacity-70 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <Textarea
                      value={child.text || ''}
                      onChange={e => updateChild(i, { ...child, text: e.target.value })}
                      placeholder={`${child.type} text...`}
                      rows={2}
                      className="text-sm resize-y bg-white/80"
                    />
                  </div>
                );
              }
              if (child.type === 'if' || child.type === 'elseIf' || child.type === 'case') {
                return (
                  <ConditionalStepEditor
                    key={child.id || i}
                    item={child}
                    onUpdate={(c) => updateChild(i, c)}
                    onDelete={() => deleteChild(i)}
                    dmlEntries={dmlEntries}
                    dirFiles={dirFiles}
                    internalTargets={internalTargets}
                  />
                );
              }
              if (child.type === 'figure') {
                return (
                  <div key={child.id || i} className="p-3 rounded border border-slate-200 bg-slate-50 space-y-2 group" data-testid={`drill-figure-${child.id}`}>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-[10px] bg-slate-100">figure</Badge>
                      <button onClick={() => deleteChild(i)} className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <Input
                      value={child.title || ''}
                      onChange={e => updateChild(i, { ...child, title: e.target.value })}
                      placeholder="Figure title..."
                      className="text-sm h-8"
                      data-testid={`figure-title-${child.id}`}
                    />
                    {dirIcns.length > 0 && (
                      <select
                        className="w-full h-8 text-sm border border-slate-200 rounded-md px-2 bg-white text-slate-700"
                        value={dirIcns.some(x => x.base === child.icn) ? child.icn : ''}
                        onChange={e => e.target.value && updateChild(i, { ...child, icn: e.target.value })}
                        data-testid={`figure-icn-picker-${child.id}`}
                      >
                        <option value="">Select ICN from working directory… ({dirIcns.length} found)</option>
                        {dirIcns.map(x => <option key={x.name} value={x.base}>{x.name}</option>)}
                      </select>
                    )}
                    <Input
                      value={child.icn || ''}
                      onChange={e => updateChild(i, { ...child, icn: e.target.value })}
                      placeholder="ICN reference (e.g. ICN-07GB6-CI001-001-01)"
                      className="text-sm h-8 font-mono"
                      data-testid={`figure-icn-${child.id}`}
                    />
                    <HotspotEditor element={child} onUpdate={(f) => updateChild(i, f)} dirIcns={dirIcns} internalTargets={internalTargets} dmlEntries={dmlEntries} dirFiles={dirFiles} />
                  </div>
                );
              }
              if (child.type === 'table') {
                return (
                  <CalsTableEditor
                    key={child.id || i}
                    element={child}
                    onUpdate={(t) => updateChild(i, t)}
                    onDelete={() => deleteChild(i)}
                    compact
                  />
                );
              }
              if (child.type === 'captionGroup') {
                return (
                  <CaptionGroupEditor
                    key={child.id || i}
                    element={child}
                    onUpdate={(c) => updateChild(i, c)}
                    onDelete={() => deleteChild(i)}
                    compact
                  />
                );
              }
              return null;
            })}
          </div>

          {/* Add child buttons */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-dashed border-violet-200">
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-slate-600" onClick={() => addChild('para')} data-testid={`add-para-${index}`}>
              <Plus className="w-3 h-3" /> Para
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-violet-600" onClick={() => addChild('step')} data-testid={`add-drill-step-${index}`}>
              <Plus className="w-3 h-3" /> <MessageSquare className="w-3 h-3" /> Step
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-teal-600" onClick={() => addChild('subCrewDrill')} data-testid={`add-sub-drill-${index}`}>
              <Plus className="w-3 h-3" /> <ListChecks className="w-3 h-3" /> Sub-Drill
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-red-500" onClick={() => addChild('warning')}>
              <Plus className="w-3 h-3" /> Warning
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-amber-500" onClick={() => addChild('caution')}>
              <Plus className="w-3 h-3" /> Caution
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-blue-500" onClick={() => addChild('note')} data-testid={`add-note-${index}`}>
              <Plus className="w-3 h-3" /> Note
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-yellow-600" onClick={() => addChild('if')}>
              <Plus className="w-3 h-3" /> <GitBranch className="w-3 h-3" /> If
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-orange-500" onClick={() => addChild('elseIf')}>
              <Plus className="w-3 h-3" /> Else If
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-purple-600" onClick={() => addChild('case')} data-testid={`add-case-${index}`}>
              <Plus className="w-3 h-3" /> <GitBranch className="w-3 h-3" /> Case
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-blue-600" onClick={() => addChild('figure')} data-testid={`add-figure-${index}`}>
              <Plus className="w-3 h-3" /> <ImageIcon className="w-3 h-3" /> Figure
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-slate-500" onClick={() => addChild('table')} data-testid={`add-table-${index}`}>
              <Plus className="w-3 h-3" /> <Table2 className="w-3 h-3" /> Table
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-emerald-600" onClick={() => addChild('captionGroup')} data-testid={`add-caption-${index}`}>
              <Plus className="w-3 h-3" /> <LayoutGrid className="w-3 h-3" /> captionGroup
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   CREW REF CARD EDITOR (main container)
   ═══════════════════════════════════════════ */
export const CrewRefCardEditor = ({ crewTitle, crewDrills, onTitleChange, onDrillsChange, dmlEntries = [], dirFiles = [], dirIcns = [], internalTargets = [], collapsedSections = {}, onToggleSection }) => {
  const addDrill = () => {
    onDrillsChange([...(crewDrills || []), {
      id: uid(),
      title: '',
      children: []
    }]);
  };

  const updateDrill = (idx, drill) => {
    const arr = [...(crewDrills || [])];
    arr[idx] = drill;
    onDrillsChange(arr);
  };

  const deleteDrill = (idx) => {
    onDrillsChange((crewDrills || []).filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-4" data-testid="crew-ref-card-editor">
      {/* Crew Ref Card Title */}
      <Card className="border-violet-200">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Badge className="bg-violet-100 text-violet-700 text-[10px]">crewRefCard</Badge>
            <span className="text-sm font-semibold text-slate-700">Reference Card Title</span>
          </div>
          <Input
            value={crewTitle || ''}
            onChange={e => onTitleChange(e.target.value)}
            placeholder="Reference card title (e.g. Pre-ride inspection)..."
            className="text-sm font-medium"
            data-testid="crew-ref-card-title"
          />
        </CardContent>
      </Card>

      {/* Drills header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Crew Drills
        </h3>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={addDrill} data-testid="add-crew-drill-btn">
          <Plus className="w-3.5 h-3.5" /> Add Drill
        </Button>
      </div>

      {/* Drills list */}
      {(crewDrills || []).length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="p-10 text-center">
            <Shield className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No crew drills yet</p>
            <p className="text-slate-400 text-xs mt-1">Click &apos;Add Drill&apos; to start building the reference card</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {(crewDrills || []).map((drill, i) => (
            <CrewDrillEditor
              key={drill.id || i}
              drill={drill}
              index={i}
              onUpdate={(d) => updateDrill(i, d)}
              onDelete={() => deleteDrill(i)}
              dmlEntries={dmlEntries}
              dirFiles={dirFiles}
              dirIcns={dirIcns}
              internalTargets={internalTargets}
              collapsed={!!collapsedSections[`drill-${drill.id || i}`]}
              onToggleCollapse={onToggleSection ? () => onToggleSection(`drill-${drill.id || i}`) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   CREW TYPE SELECTOR
   Choose between descrCrew and crewRefCard
   ═══════════════════════════════════════════ */
export const CrewTypeSelector = ({ crewType, onChange }) => (
  <div className="space-y-1" data-testid="crew-type-selector">
    <Label className="text-xs text-slate-500">Crew Content Type</Label>
    <Select value={crewType || 'crewRefCard'} onValueChange={onChange}>
      <SelectTrigger className="h-8 text-sm" data-testid="crew-type-select">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="descrCrew">descrCrew (Descriptive Text)</SelectItem>
        <SelectItem value="crewRefCard">crewRefCard (Reference Card)</SelectItem>
      </SelectContent>
    </Select>
  </div>
);

/* ═══════════════════════════════════════════
   PARSE CREW XML from DOM document
   Called from parseXmlFile when <crew> detected
   ═══════════════════════════════════════════ */
/* ── BRDP id normalization for crew drills/steps ──
   crewDrill → cre-0001, crewDrillStep → cds-0001 (cds disambiguates from crewDrill's 'cre') */
const needsSpecId = (id) => !id || !id.includes('-');
const _scanMax = (id, prefix, cur) => {
  const m = new RegExp(`^${prefix}-(\\d{4,})$`).exec(id || '');
  return m ? Math.max(cur, parseInt(m[1], 10)) : cur;
};

export function normalizeCrewIds(drills, counters = null) {
  const c = counters || { cre: 0, cds: 0 };
  if (!counters) {
    (drills || []).forEach(d => {
      c.cre = _scanMax(d.id, 'cre', c.cre);
      (d.children || []).forEach(ch => {
        if (ch.type === 'step') c.cds = _scanMax(ch.id, 'cds', c.cds);
        if (ch.type === 'subCrewDrill') (ch.steps || []).forEach(s => { c.cds = _scanMax(s.id, 'cds', c.cds); });
      });
    });
  }
  const fixStep = (s) => needsSpecId(s.id) ? { ...s, id: `cds-${String(++c.cds).padStart(4, '0')}` } : s;
  return (drills || []).map(d => {
    const nd = needsSpecId(d.id) ? { ...d, id: `cre-${String(++c.cre).padStart(4, '0')}` } : d;
    return {
      ...nd,
      children: (nd.children || []).map(ch => {
        if (ch.type === 'step') return fixStep(ch);
        if (ch.type === 'subCrewDrill') return { ...ch, steps: (ch.steps || []).map(fixStep) };
        return ch;
      }),
    };
  });
}

// descrCrew: normalize crewDrill/step ids nested inside levelledPara elements (recursive)
export function normalizeLpCrewIds(lps) {
  const c = { cre: 0, cds: 0 };
  const scanDrill = (d) => {
    c.cre = _scanMax(d.id, 'cre', c.cre);
    (d.children || []).forEach(ch => {
      if (ch.type === 'step') c.cds = _scanMax(ch.id, 'cds', c.cds);
      if (ch.type === 'subCrewDrill') (ch.steps || []).forEach(s => { c.cds = _scanMax(s.id, 'cds', c.cds); });
    });
  };
  const walkScan = (lp) => (lp.elements || []).forEach(el => {
    if (el.type === 'crewDrill' && el.drill) scanDrill(el.drill);
    if (el.type === 'levelledPara' && el.lp) walkScan(el.lp);
  });
  (lps || []).forEach(walkScan);
  const walkFix = (lp) => ({
    ...lp,
    elements: (lp.elements || []).map(el => {
      if (el.type === 'crewDrill' && el.drill) return { ...el, drill: normalizeCrewIds([el.drill], c)[0] };
      if (el.type === 'levelledPara' && el.lp) return { ...el, lp: walkFix(el.lp) };
      return el;
    }),
  });
  return (lps || []).map(walkFix);
}

// Collect internalRef targets (drills + steps) from a drills array
export function collectCrewTargets(drills, targets, tt) {
  (drills || []).forEach(d => {
    if (d.id && !needsSpecId(d.id)) targets.push({ id: d.id, targetType: tt('irtt07', 'para'), label: `Crew drill — ${d.title || d.id}` });
    let sn = 0;
    const stepLabel = (s) => (s.challenge || s.procedureName || s.paraText || '').slice(0, 40);
    (d.children || []).forEach(ch => {
      if (ch.type === 'step' && ch.id && !needsSpecId(ch.id)) {
        sn += 1;
        targets.push({ id: ch.id, targetType: tt('irtt08', 'step'), label: `Drill step ${sn}${stepLabel(ch) ? ` — ${stepLabel(ch)}` : ''} (${d.title || 'drill'})` });
      }
      if (ch.type === 'figure') {
        if (ch.id && !needsSpecId(ch.id)) targets.push({ id: ch.id, targetType: tt('irtt01', 'figure'), label: `Figure — ${ch.title || ch.id} (${d.title || 'drill'})` });
        (ch.hotspots || []).forEach(h => {
          if (h.id) targets.push({ id: h.id, targetType: tt('irtt11', 'hotspot'), label: `Hotspot — ${h.title || h.id}` });
        });
      }
      if (ch.type === 'subCrewDrill') (ch.steps || []).forEach(s => {
        if (s.id && !needsSpecId(s.id)) {
          sn += 1;
          targets.push({ id: s.id, targetType: tt('irtt08', 'step'), label: `Drill step ${sn}${stepLabel(s) ? ` — ${stepLabel(s)}` : ''} (${ch.title || 'sub-drill'})` });
        }
      });
    });
  });
}


export function parseCrewXml(doc) {
  const crewEl = doc.querySelector('content crew');
  if (!crewEl) return null;

  const descrCrewEl = crewEl.querySelector(':scope > descrCrew');
  const crewRefCardEl = crewEl.querySelector(':scope > crewRefCard');

  if (descrCrewEl) {
    // descrCrew mode — parse levelledParas (same as descriptive)
    return { crewType: 'descrCrew', crewTitle: '', crewDrills: [], hasDescrCrew: true };
  }

  if (crewRefCardEl) {
    const crewTitle = crewRefCardEl.querySelector(':scope > title')?.textContent || '';
    const crewDrills = [];

    crewRefCardEl.querySelectorAll(':scope > crewDrill').forEach(drillEl => {
      crewDrills.push(parseCrewDrill(drillEl));
    });

    return { crewType: 'crewRefCard', crewTitle, crewDrills, hasDescrCrew: false };
  }

  return null;
}

export function parseCrewDrill(drillEl) {
  const drill = {
    id: drillEl.getAttribute('id') || uid(),
    title: drillEl.querySelector(':scope > title')?.textContent || '',
    children: []
  };

  // Process direct children in order
  for (const child of drillEl.children) {
    if (child.tagName === 'title') continue;

    if (child.tagName === 'crewDrillStep') {
      drill.children.push(parseDrillStep(child));
    } else if (child.tagName === 'subCrewDrill') {
      drill.children.push(parseSubCrewDrill(child));
    } else if (child.tagName === 'warning') {
      const wp = child.querySelector('warningAndCautionPara');
      drill.children.push({ type: 'warning', id: uid(), text: wp?.textContent?.trim() || '' });
    } else if (child.tagName === 'caution') {
      const cp = child.querySelector('warningAndCautionPara');
      drill.children.push({ type: 'caution', id: uid(), text: cp?.textContent?.trim() || '' });
    } else if (child.tagName === 'note') {
      const np = child.querySelector('notePara');
      drill.children.push({ type: 'note', id: uid(), text: np?.textContent?.trim() || '' });
    } else if (child.tagName === 'para') {
      drill.children.push({ type: 'para', id: uid(), text: child.textContent?.trim() || '' });
    } else if (child.tagName === 'if') {
      drill.children.push(parseConditional(child, 'if'));
    } else if (child.tagName === 'elseIf') {
      drill.children.push(parseConditional(child, 'elseIf'));
    } else if (child.tagName === 'case') {
      drill.children.push(parseConditional(child, 'case'));
    } else if (child.tagName === 'figure') {
      const figTitle = child.querySelector(':scope > title');
      const graphic = child.querySelector(':scope > graphic');
      const hotspots = graphic ? Array.from(graphic.querySelectorAll(':scope > hotspot')).map(parseHotspotEl) : [];
      drill.children.push({
        type: 'figure',
        id: child.getAttribute('id') || uid(),
        title: figTitle?.textContent || '',
        icn: graphic?.getAttribute('infoEntityIdent') || '',
        hotspots: hotspots.length > 0 ? hotspots : undefined,
      });
    } else if (child.tagName === 'table') {
      drill.children.push(parseCalsTable(child));
    } else if (child.tagName === 'captionGroup') {
      drill.children.push(parseCaptionGroup(child));
    }
  }

  return drill;
}

function parseDrillStep(stepEl) {
  const stepId = stepEl.getAttribute('id') || uid();
  const car = stepEl.querySelector(':scope > challengeAndResponse');
  const cpn = stepEl.querySelector(':scope > crewProcedureName');
  const directPara = stepEl.querySelector(':scope > para');

  if (car) {
    const challengeEl = car.querySelector(':scope > challenge');
    const responseEl = car.querySelector(':scope > response');

    // Helper: get only direct text content from a <para>, excluding captionGroup descendant text
    const getParaText = (parentEl) => {
      const para = parentEl?.querySelector(':scope > para');
      if (!para) return '';
      let text = '';
      for (const node of para.childNodes) {
        if (node.nodeType === 3) text += node.textContent; // TEXT_NODE only
      }
      return text.trim();
    };

    const challenge = getParaText(challengeEl);
    const response = getParaText(responseEl);

    // S1000D: captionGroups live INSIDE <para>, not as siblings
    const challengePara = challengeEl?.querySelector(':scope > para');
    const responsePara = responseEl?.querySelector(':scope > para');

    const challengeCGs = [];
    challengePara?.querySelectorAll(':scope > captionGroup').forEach(cg => challengeCGs.push(parseCaptionGroup(cg)));
    const responseCGs = [];
    responsePara?.querySelectorAll(':scope > captionGroup').forEach(cg => responseCGs.push(parseCaptionGroup(cg)));

    // Fallback: also check as direct child of response/challenge (backward compat with older XML)
    if (challengeCGs.length === 0) {
      const cg = challengeEl?.querySelector(':scope > captionGroup');
      if (cg) challengeCGs.push(parseCaptionGroup(cg));
    }
    if (responseCGs.length === 0) {
      const cg = responseEl?.querySelector(':scope > captionGroup');
      if (cg) responseCGs.push(parseCaptionGroup(cg));
    }

    // Tables: check inside para first, then direct child
    const challengeTable = challengePara?.querySelector(':scope > table') || challengeEl?.querySelector(':scope > table');
    const responseTable = responsePara?.querySelector(':scope > table') || responseEl?.querySelector(':scope > table');

    const step = { type: 'step', id: stepId, stepType: 'challengeAndResponse', challenge, response };
    if (challengeCGs.length > 0) step.challengeCaptionGroups = challengeCGs;
    if (responseCGs.length > 0) step.responseCaptionGroups = responseCGs;
    if (challengeTable) step.challengeTable = parseCalsTable(challengeTable);
    if (responseTable) step.responseTable = parseCalsTable(responseTable);
    return step;
  }
  if (cpn) {
    const procedureName = cpn.querySelector('para')?.textContent?.trim() || '';
    return { type: 'step', id: stepId, stepType: 'crewProcedureName', procedureName };
  }
  if (directPara) {
    const paraText = directPara.textContent?.trim() || '';
    return { type: 'step', id: stepId, stepType: 'para', paraText };
  }
  return { type: 'step', id: stepId, stepType: 'para', paraText: '' };
}

function parseSubCrewDrill(el) {
  const title = el.querySelector(':scope > title')?.textContent || '';
  const steps = [];
  el.querySelectorAll(':scope > crewDrillStep').forEach(stepEl => {
    steps.push(parseDrillStep(stepEl));
  });
  return { type: 'subCrewDrill', id: uid(), title, steps };
}

function parseConditional(el, type) {
  const condition = el.querySelector(':scope > caseCond')?.textContent?.trim() || '';
  const stepEl = el.querySelector(':scope > crewDrillStep');
  let stepType = 'challengeAndResponse';
  let challenge = '', response = '', procedureName = '', paraText = '';
  let challengeCaptionGroups, responseCaptionGroups, challengeTable, responseTable;

  if (stepEl) {
    const parsed = parseDrillStep(stepEl);
    stepType = parsed.stepType;
    challenge = parsed.challenge || '';
    response = parsed.response || '';
    procedureName = parsed.procedureName || '';
    paraText = parsed.paraText || '';
    challengeCaptionGroups = parsed.challengeCaptionGroups;
    responseCaptionGroups = parsed.responseCaptionGroups;
    challengeTable = parsed.challengeTable;
    responseTable = parsed.responseTable;
  }

  const result = { type, id: uid(), condition, stepType, challenge, response, procedureName, paraText };
  if (challengeCaptionGroups) result.challengeCaptionGroups = challengeCaptionGroups;
  if (responseCaptionGroups) result.responseCaptionGroups = responseCaptionGroups;
  if (challengeTable) result.challengeTable = challengeTable;
  if (responseTable) result.responseTable = responseTable;
  return result;
}
